import React, { useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '../../../../store/hooks';
import { Text } from '../../../../components/Typography/Typography';
import { Button } from '../../../../components/UI';
import { getConversationById, getConversationHistory } from '../../../../api/chatApi';
import { ConversationPreview, ConversationStatus } from '../../../../store/chat/types';
import {
  loadConversation,
  setConversationHistory,
  setIsLoadingHistory,
  setIsLoadingConversation,
  clearConversation,
  setConversationStats,
  addMessage,
  createErrorMessage,
  updateConversationInHistory,
} from '../../../../store/chat/slice';
import ConversationList from './components/ConversationList';
import styles from './ChatHistory.module.scss';
import { sendConversationStatus } from '../../../../socket/publishers/sendConversationStatus';
import IconClose from '../../../../components/Icon/IconClose';
import { setSelectedCompanies } from '../../../../store/company/slice';

interface ChatHistoryProps {
  isMobile: boolean;
  onClose: () => void;
}

const ChatHistory: React.FC<ChatHistoryProps> = ({ onClose, isMobile }) => {
  const dispatch = useAppDispatch();
  const { conversation, conversationHistory, isLoadingHistory } = useAppSelector(
    (state) => state.chat,
  );
  const [error, setError] = React.useState<string | null>(null);

  useEffect(() => {
    fetchConversations();
  }, []);

  const fetchConversations = async () => {
    dispatch(setIsLoadingHistory(true));
    setError(null);

    try {
      const response = await getConversationHistory();
      dispatch(
        setConversationHistory({
          conversations: response.conversations,
        }),
      );
      dispatch(
        setConversationStats({
          dailyMessageCount: response.dailyMessageCount,
          limitHitTime: response.limitHitTime,
          resetTime: response.resetTime,
        }),
      );
    } catch (err) {
      console.error('Failed to fetch conversations:', err);
      setError('Failed to load conversations');
    } finally {
      dispatch(setIsLoadingHistory(false));
    }
  };

  const handleSelectConversation = async (selectedConversation: ConversationPreview) => {
    try {
      if (conversation?.id === selectedConversation.id) {
        return;
      }

      dispatch(setIsLoadingConversation(true));

      const response = await getConversationById(selectedConversation.id);
      const status = response.conversation.status;

      dispatch(loadConversation(response.conversation));

      // If the conversation was error status, append an error message
      if (status === ConversationStatus.Error) {
        const errorMessage = createErrorMessage(
          'An error occurred with this conversation. Please try asking your question again.',
        );
        dispatch(addMessage(errorMessage));
      }

      // If the conversation has unread messages, mark it as seen
      if (status === ConversationStatus.NewAnswer || status === ConversationStatus.Error) {
        await sendConversationStatus(selectedConversation.id, ConversationStatus.Inactive);
        dispatch(
          updateConversationInHistory({
            id: selectedConversation.id,
            updates: { status: ConversationStatus.Inactive },
          }),
        );
      }
    } catch (err) {
      console.error('Failed to load conversation:', err);
      setError('Failed to load conversation');
    } finally {
      dispatch(setIsLoadingConversation(false));
    }
  };

  const handleNewConversation = () => {
    dispatch(clearConversation());
    dispatch(setSelectedCompanies([]));
  };

  return (
    <div className={styles.container}>
      {isMobile && (
        <Button
          variant="ghost"
          size="small"
          onClick={onClose}
          aria-label="Close"
          icon={<IconClose />}
          className={styles.closeButton}
        />
      )}
      <div className={styles.header}>
        <Text weight="bold" size="large">
          Chat History
        </Text>
        <Button
          variant="ghost"
          size="small"
          className={styles.newChatButton}
          onClick={handleNewConversation}
          aria-label="New conversation"
        >
          +
        </Button>
      </div>

      {error && (
        <div className={styles.error}>
          <Text color="red">{error}</Text>
        </div>
      )}

      <div className={styles.historyList}>
        <ConversationList
          conversations={conversationHistory}
          currentConversation={conversation}
          onSelectConversation={handleSelectConversation}
          isLoading={isLoadingHistory}
        />
      </div>
    </div>
  );
};

export default ChatHistory;
